/**
 * Требования к паролю — ВИДНЫ ДО ВВОДА (1-Э1.3).
 *
 * ПОЧЕМУ ЭТО ВАЖНО. Правило, показанное после отказа, читается как придирка: человек
 * придумал пароль, ввёл дважды, и ему сказали «нет». Правило, показанное до ввода,
 * — это условие задачи. Поэтому список стоит над полем и отмечает выполненное
 * по мере набора.
 *
 * СПИСОК ПОВТОРЯЕТ СЕРВЕРНУЮ ПРОВЕРКУ (apps/api/src/modules/auth/password.service.ts
 * и leaked-passwords.ts), но НЕ заменяет её: клиент показывает, сервер решает. Любое
 * расхождение — это отказ с сервера, и текст отказа там же и написан.
 */

export interface PasswordRule {
  readonly key: "length" | "variety" | "not-identifier" | "not-leaked"
  readonly text: string
  /** Выполнено ли правило на текущем вводе. */
  readonly met: (password: string, identifier: string) => boolean
}

/**
 * Основы самых частых паролей.
 *
 * СПИСОК ПОВТОРЯЕТ СЕРВЕРНЫЙ (apps/api/src/modules/auth/leaked-passwords.ts) — и это
 * не дублирование ради удобства. Пока он был вчетверо короче, человек видел четыре
 * зелёные галочки и получал отказ: экран обещал, что пароль подходит, а сервер отвечал
 * «нет». Расхождение здесь не «неточность подсказки», а прямая ложь интерфейса.
 */
const COMMON_STEMS: ReadonlySet<string> = new Set([
  "password",
  "qwerty",
  "qwertyuiop",
  "йцукен",
  "пароль",
  "привет",
  "любовь",
  "admin",
  "administrator",
  "welcome",
  "letmein",
  "monkey",
  "dragon",
  "football",
  "iloveyou",
  "sunshine",
  "princess",
  "солнышко",
  "солнце",
  "зайка",
  "котенок",
  "красота",
  "россия",
  "москва",
  "спартак",
  "зенит",
  "салон",
  "барбершоп",
  "мастер",
  "маникюр",
  "стрижка",
  "администратор",
])

/** Подряд идущие символы: «1234567890», «abcdefghij», «йцукенгшщз». */
const SEQUENCES: readonly string[] = [
  "0123456789",
  "9876543210",
  "abcdefghijklmnopqrstuvwxyz",
  "zyxwvutsrqponmlkjihgfedcba",
  "йцукенгшщзхъфывапролджэячсмитьбю",
]

function stem(password: string): string {
  return password
    .trim()
    .toLowerCase()
    .replace(/[\d\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]+$/u, "")
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    key: "length",
    text: "Не меньше 10 символов",
    met: (password) => password.trim().length >= 10,
  },
  {
    key: "variety",
    text: "Хотя бы четыре разных символа",
    met: (password) => new Set(password.trim().toLowerCase()).size >= 4,
  },
  {
    key: "not-identifier",
    text: "Не совпадает с вашей почтой или телефоном",
    met: (password, identifier) =>
      identifier.trim().length === 0 ||
      password.trim().toLowerCase() !== identifier.trim().toLowerCase(),
  },
  {
    key: "not-leaked",
    text: "Не из списка утёкших: «qwerty1234», «пароль123» подбирают первыми",
    met: (password) => {
      const base = stem(password)
      if (base.length >= 4 && COMMON_STEMS.has(base)) return false
      const normalized = password.trim().toLowerCase()
      if (normalized.length >= 6 && /^(.)\1+$/u.test(normalized)) return false
      if (normalized.length >= 6 && SEQUENCES.some((row) => row.includes(normalized))) return false
      // Пароль из одних цифр подбирают перебором за минуты, какой бы длины он ни был:
      // проверка «входит в 0123456789» ловила «123456», но пропускала «1234567890».
      return !/^\d+$/u.test(normalized)
    },
  },
]

export function allRulesMet(password: string, identifier: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.met(password, identifier))
}

/**
 * Правила, которые экран может проверить САМ, зная только пароль.
 *
 * На экране нового пароля почты человека нет: он пришёл по ссылке из письма, и кто он —
 * знает сервер, а не экран. Правило «не совпадает с почтой или телефоном» стояло там
 * с вечной зелёной галочкой — то есть экран утверждал, что проверил то, чего проверить
 * не мог. Отмечать можно только проверенное; остальное честно названо условием,
 * которое проверит сервер (см. IDENTIFIER_NOTE).
 */
export function checkableRules(identifier: string): readonly PasswordRule[] {
  if (identifier.trim().length > 0) return PASSWORD_RULES
  return PASSWORD_RULES.filter((rule) => rule.key !== "not-identifier")
}

/** Условие, которое проверяет сервер: экран не знает, под каким адресом живёт человек. */
export const IDENTIFIER_NOTE =
  "Пароль не должен совпадать с вашей почтой или телефоном — это проверит сервер."


/**
 * Два последствия успешной смены пароля — их человек должен прочитать ДО того, как
 * обнаружит сам (1-Э1.3).
 *
 * Первое: выходят все устройства, включая телефон в кармане. Второе: сутки закрыта
 * выгрузка базы — защита от «увёл доступ и сразу выгрузил клиентов».
 */
export const RESET_DONE_CONSEQUENCES: readonly string[] = [
  "Вход на других устройствах завершён — на телефоне и планшете придётся войти заново.",
  "Выгрузка базы клиентов недоступна сутки: так устроена защита от угона доступа.",
]
